import { Children } from 'react';
import { cleanDisplayText } from '@/utils/display-text';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { AuiRendererProps } from '../registry';
import { useSurfaceNodeUiState } from '../AuiSurfaceContext';

interface PersistedTabsSlice {
  activeTab: string;
}

export function AuiTabs({ node, children, childNodes }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const explicitTabs = props.tabs as { id: string; label: string }[] | undefined;

  // Fallback: derive tab metadata from child nodes' props.label (or id).
  // Lets agents author A2UI-style tabs by listing children + putting labels on them.
  const derivedTabs = childNodes?.map((child, index) => ({
    id: child.id,
    label:
      (child.component.props?.label as string | undefined) ??
      child.id ??
      `Tab ${index + 1}`,
  })) ?? [];

  const tabs = explicitTabs && explicitTabs.length > 0 ? explicitTabs : derivedTabs;
  const defaultTab = (props.defaultTab as string) || tabs[0]?.id || '';

  const childArray = Children.toArray(children);

  // Persist the active tab. activeTab is computed synchronously so we never
  // pass `undefined` to Radix's controlled `value` prop. The hook is called
  // unconditionally to satisfy rules-of-hooks; if tabs is empty we still
  // early-return below without rendering Radix.
  const [persisted, setPersisted] = useSurfaceNodeUiState<'tabs', PersistedTabsSlice>(
    node.id,
    'tabs',
    { activeTab: defaultTab },
  );

  if (tabs.length === 0) {
    return <div data-testid="aui-tabs" />;
  }

  const activeTab = tabs.some((t) => t.id === persisted.activeTab)
    ? persisted.activeTab
    : defaultTab;

  return (
    <TabsPrimitive.Root
      data-testid="aui-tabs"
      value={activeTab}
      onValueChange={(v) => setPersisted({ activeTab: v })}
    >
      <TabsPrimitive.List className="w-full inline-flex h-10 items-center justify-start rounded-none border-b bg-transparent p-0 text-muted-foreground">
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.id}
            value={tab.id}
            // Visible chrome stays square (rounded-none + border-b-2 active
            // indicator). The focus ring is rendered via an ::after pseudo
            // element with rounded-md so we can have a rounded outline
            // independent of the trigger's own border-radius — Tailwind's
            // `ring` follows the element's radius, which would force the
            // trigger to be rounded too.
            className="relative inline-flex items-center justify-center whitespace-nowrap rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-none focus-visible:outline-none after:pointer-events-none after:absolute after:inset-[-3px] after:rounded-md after:border after:border-transparent after:content-[''] focus-visible:after:border-muted-foreground/50 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
          >
            {cleanDisplayText(tab.label)}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {tabs.map((tab, index) => (
        <TabsPrimitive.Content
          key={tab.id}
          value={tab.id}
          // Radix sets tabIndex={0} on the panel by default, making it a
          // focus stop after the active trigger. Keyboard order:
          // trigger → panel (rounded gray ring) → first interactive child
          // inside the panel, then on through the rest of the panel's
          // focusable descendants in normal DOM order.
          className="mt-4 rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/50 focus-visible:ring-offset-[12px] focus-visible:ring-offset-background"
        >
          {childArray[index] ?? null}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
}
